/**
 * In-memory mnemonic state provider with multi-tab coordination.
 *
 * Holds the decrypted master mnemonic in React state (memory only) during
 * an active session. The mnemonic is NEVER written to disk by this provider —
 * it exists only in memory while the user is actively using the app.
 *
 * Multi-tab coordination (via BroadcastChannel) ensures only one tab holds
 * the decrypted mnemonic at a time. When a second tab unlocks, the first
 * tab automatically locks (last-writer-wins strategy).
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module MnemonicProvider
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createTabCoordinator } from '../lib/tab-coordinator';
import type { TabCoordinator } from '../lib/tab-coordinator';

/**
 * Value provided by MnemonicContext.
 */
export interface MnemonicContextValue {
  /** The decrypted mnemonic, or null when locked. */
  readonly mnemonic: string | null;
  /** Whether a mnemonic is currently held in memory. */
  readonly isUnlocked: boolean;
  /** Store the mnemonic in memory and claim the session across tabs. */
  unlock(mnemonic: string): void;
  /** Clear the mnemonic from memory and release the session. */
  lock(): void;
}

const MnemonicContext = createContext<MnemonicContextValue | null>(null);

/**
 * Generate a unique tab identifier.
 *
 * Uses crypto.randomUUID when available, falls back to Math.random.
 */
function generateTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tab-${Math.random().toString(36).slice(2)}`;
}

/** Props for MnemonicProvider. */
interface MnemonicProviderProps {
  readonly children: ReactNode;
}

/**
 * Provider that manages the decrypted mnemonic in memory.
 *
 * Must wrap any component that needs access to the mnemonic.
 * Integrates with tab coordinator for multi-tab session management.
 *
 * Tier: All tiers (core infrastructure).
 */
export function MnemonicProvider({ children }: MnemonicProviderProps): ReactNode {
  const [mnemonic, setMnemonic] = useState<string | null>(null);
  const coordinatorRef = useRef<TabCoordinator | null>(null);
  const tabIdRef = useRef<string>(generateTabId());

  const lock = useCallback((): void => {
    setMnemonic(null);
    coordinatorRef.current?.releaseSession();
  }, []);

  const unlock = useCallback((newMnemonic: string): void => {
    if (!newMnemonic || newMnemonic.trim() === '') {
      throw new Error('Mnemonic must not be empty');
    }
    setMnemonic(newMnemonic);
    coordinatorRef.current?.claimSession();
  }, []);

  useEffect(() => {
    const coordinator = createTabCoordinator(tabIdRef.current);
    coordinatorRef.current = coordinator;

    // When another tab claims the session, lock this tab
    coordinator.onSessionClaimed(() => {
      setMnemonic(null);
    });

    const handleBeforeUnload = (): void => {
      coordinator.releaseSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      coordinator.releaseSession();
      coordinator.destroy();
      coordinatorRef.current = null;
    };
  }, []);

  const value: MnemonicContextValue = {
    mnemonic,
    isUnlocked: mnemonic !== null,
    unlock,
    lock,
  };

  return <MnemonicContext value={value}>{children}</MnemonicContext>;
}

/**
 * Access the mnemonic context.
 *
 * Must be called within a MnemonicProvider. Throws if used outside.
 *
 * @returns MnemonicContextValue with mnemonic state and controls
 * @throws Error if called outside MnemonicProvider
 */
export function useMnemonic(): MnemonicContextValue {
  const context = useContext(MnemonicContext);
  if (context === null) {
    throw new Error('useMnemonic must be used within a MnemonicProvider');
  }
  return context;
}
